import React, { useState, useRef, useEffect } from "react";
import ChannelIcon from "./ChannelIcon";

const ChannelSelector = ({ availableChannels, channels, selectedChannels = [], onChange, singleSelect = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedChannels, setInternalSelectedChannels] = useState(selectedChannels);
  const dropdownRef = useRef(null);
  
  // Determine which channels array to use - backwards compatibility
  const channelsArray = availableChannels || channels || [];
  
  // Update internal state when prop changes - but only when the references actually change
  useEffect(() => {
    // Deep comparison to prevent unnecessary updates
    const currentSelection = JSON.stringify(selectedChannels.sort());
    const internalSelection = JSON.stringify([...internalSelectedChannels].sort());
    
    if (currentSelection !== internalSelection) {
      setInternalSelectedChannels(selectedChannels);
    }
  }, [selectedChannels, internalSelectedChannels]);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const toggleChannel = (channel) => {
    let newSelection;
    
    if (singleSelect) {
      // For single select, just replace the selection
      // If the same channel is clicked again, deselect it (return to "All channels")
      if (internalSelectedChannels.length === 1 && internalSelectedChannels[0] === channel) {
        newSelection = [];
      } else {
        newSelection = [channel];
      }
    } else {
      // For multi-select, toggle the channel
      const isSelected = internalSelectedChannels.includes(channel);
      if (isSelected) {
        newSelection = internalSelectedChannels.filter(ch => ch !== channel);
      } else {
        newSelection = [...internalSelectedChannels, channel];
      }
    }
    
    setInternalSelectedChannels(newSelection);
    
    if (onChange) {
      onChange(newSelection);
    }
    
    // For single select, close the dropdown after selection
    if (singleSelect) {
      setIsOpen(false);
    }
  };
  
  const selectAllChannels = () => {
    if (singleSelect) return; // Not applicable for single select
    
    if (!channelsArray || !Array.isArray(channelsArray)) {
      console.error("Channels array is not properly defined");
      return;
    }
    
    const allChannels = channelsArray.map(ch => getChannelName(ch));
    setInternalSelectedChannels(allChannels);
    
    if (onChange) {
      onChange(allChannels);
    }
  };
  
  const clearAllChannels = () => {
    setInternalSelectedChannels([]);
    
    if (onChange) {
      onChange([]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  // Guard against invalid channels array
  if (!channelsArray || !Array.isArray(channelsArray) || channelsArray.length === 0) {
    return (
      <div className="inline-block">
        <button
          className="inline-flex items-center justify-between bg-grey-20 border border-grey-20 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-50"
          disabled
        >
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-black-0">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227z" />
            </svg>
            <span className="text-black-0 text-sm">No channels available</span>
          </div>
        </button>
      </div>
    );
  }
  
  // Helper function to get channel name consistently
  const getChannelName = (channel) => {
    if (!channel) return "unknown";
    return channel.name || channel.channel || String(channel.id) || "unnamed";
  };
  
  // Helper function to check if a channel is selected
  const isChannelSelected = (channel) => {
    const channelName = getChannelName(channel);
    return internalSelectedChannels.includes(channelName);
  };
  
  // Helper function to generate a unique key for each channel
  const getChannelKey = (channel) => {
    return channel.id || `channel-${getChannelName(channel)}`;
  };
  
  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="inline-flex items-center justify-between bg-grey-20 border border-grey-20 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-50"
      >
        <div className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-black-0">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227z" />
          </svg>
          <span className="text-black-0 text-sm flex items-center gap-1">
            {internalSelectedChannels.length === 0 
              ? "All channels" 
              : internalSelectedChannels.length === 1 
                ? (
                  <>
                    <ChannelIcon 
                      isPrivate={channelsArray.find(ch => getChannelName(ch) === internalSelectedChannels[0])?.is_private} 
                      size={14} 
                    />
                    {internalSelectedChannels[0]}
                  </>
                )
                : `${internalSelectedChannels.length} channels`}
          </span>
        </div>
        <svg 
          className="w-4 h-4 ml-2" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute z-[9999] mt-1 w-[360px] bg-white-f border border-grey-20 rounded-md shadow-subtle">
          {!singleSelect && (
            <div className="flex justify-between p-2 border-b border-grey-20">
              <button 
                onClick={selectAllChannels}
                className="text-xs text-grey-60 hover:text-black-0"
              >
                Select all
              </button>
              <button 
                onClick={clearAllChannels}
                className="text-xs text-grey-60 hover:text-black-0"
              >
                Clear all
              </button>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto">
            {channelsArray.map(channel => {
              const channelName = getChannelName(channel);
              const channelKey = getChannelKey(channel);
              
              return (
                <label 
                  key={channelKey}
                  className="flex items-center px-4 py-2 hover:bg-grey-10 cursor-pointer"
                >
                  <input
                    type={singleSelect ? "radio" : "checkbox"}
                    className="mr-2"
                    checked={isChannelSelected(channel)}
                    onChange={() => toggleChannel(channelName)}
                    name={singleSelect ? "channelSelection" : undefined}
                  />
                  <div className="flex items-center gap-1">
                    <ChannelIcon isPrivate={channel.is_private} size={14} />
                    <span className="text-sm text-black-0">{channelName}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelSelector; 